import React from 'react'
import { IBadgeProps } from './interfaces'
import { PushlyAi } from '@pushly/aqe/lib/components'
import { Tooltip, Popover } from 'antd'
import './ai-generated-badge.scss'
import { titleCase } from 'change-case'

/**
 * Metadata about AI content generation including tone and reasoning.
 * This information is displayed in detail views to provide context about
 * how the AI generated the notification content.
 */
interface ISourceMetadata {
    /** The reason why the AI generated this specific content */
    reason?: string
    /** The tone used for content generation (e.g., "professional", "casual") */
    tone?: string
}

/**
 * Complete structure of AI-generated field suggestions.
 * Used for backward compatibility checking when source field may be missing.
 */
interface IAutoSuggestedFieldsResults {
    /** The AI tool that generated the content: 'pushly_ai' or 'page_analyzer' */
    source?: string | null
    /** Additional metadata about the AI generation process */
    sourceMetadata?: ISourceMetadata | null
    /** AI-generated notification title */
    title?: string | null
    /** AI-generated notification body text */
    body?: string | null
    /** AI-generated image URL */
    imageUrl?: string | null
    /** AI-generated keywords for targeting */
    keywords?: string[] | null
}

/**
 * Props for the AI Generated Badge component.
 *
 * @example
 * // List view with simple tooltip
 * <AiGeneratedBadge source="pushly_ai" showMetadata={false} />
 *
 * @example
 * // Detail view with enhanced popover showing metadata
 * <AiGeneratedBadge
 *   source="page_analyzer"
 *   sourceMetadata={{ tone: "professional", reason: "Based on page content" }}
 *   showMetadata={true}
 * />
 */
interface IAiGeneratedBadgeProps extends IBadgeProps {
    /** The AI tool that generated the content: 'pushly_ai' or 'page_analyzer' */
    source?: string | null
    /** Additional metadata about AI generation (tone, reason) displayed in detail views */
    sourceMetadata?: ISourceMetadata | null
    /**
     * If true, shows enhanced popover with metadata (for detail views).
     * If false, shows simple tooltip with source name (for list views).
     */
    showMetadata?: boolean
    /**
     * Optional: Pass full autoSuggestedFieldsResults for backward compatibility handling.
     * When provided, the component will check if legacy notifications have AI content
     * but no source field, and default to 'page_analyzer'.
     */
    autoSuggestedFieldsResults?: IAutoSuggestedFieldsResults | null
}

/**
 * Badge component that displays a visual indicator when notification content
 * was generated using AI tools (Pushly AI or Page Analyzer).
 *
 * The badge shows different levels of detail based on context:
 * - List views: Simple tooltip with AI source name
 * - Detail views: Enhanced popover with source, tone, and reason
 *
 * Backward Compatibility:
 * For legacy notifications created before the source field was added,
 * the component checks if AI content exists (title, body, imageUrl, or keywords)
 * and defaults to 'page_analyzer' as the source.
 *
 * @param props - Component props including source, metadata, and display options
 * @returns Badge element with PushlyAi icon and appropriate tooltip/popover, or null if no valid source
 */
export const AiGeneratedBadge: React.FC<React.PropsWithChildren<IAiGeneratedBadgeProps>> = (props) => {
    // Determine the source, applying backward compatibility logic if needed
    let source = props.source
    let sourceMetadata = props.sourceMetadata

    // Backward compatibility: if autoSuggestedFieldsResults is provided,
    // check if content exists but source is missing, and default to 'page_analyzer'
    if (props.autoSuggestedFieldsResults) {
        const results = props.autoSuggestedFieldsResults
        const hasContent =
            results.title || results.body || results.imageUrl || (results.keywords && results.keywords.length > 0)

        if (hasContent && !results.source) {
            // Legacy notification with AI content but no source field - default to page_analyzer
            // This ensures notifications created before the source field was added still display the badge
            source = 'page_analyzer'
        } else {
            // Use the source from autoSuggestedFieldsResults if available
            source = results.source || source
        }

        // Use metadata from autoSuggestedFieldsResults if available
        sourceMetadata = results.sourceMetadata || sourceMetadata
    }

    // Validate source - only display badge for recognized AI tools
    const validSource = source === 'pushly_ai' || source === 'page_analyzer'
    if (!validSource) {
        return null
    }

    // Convert source identifier to human-readable name
    const sourceName = source === 'pushly_ai' ? 'Pushly AI' : 'Page Analyzer'
    const basicTooltip = `Content generated by ${sourceName}`

    const badge = (
        <span className="icon-pushly-ai">
            <PushlyAi />
        </span>
    )

    const hideMetadata = props.showMetadata === false || (!sourceMetadata?.tone && !sourceMetadata?.reason)

    // List view: simple tooltip with source name only
    if (hideMetadata || props.hideTooltip) {
        return (
            <span className="ai-generated-badge">
                {props.hideTooltip ? badge : <Tooltip title={props.tooltip || basicTooltip}>{badge}</Tooltip>}
            </span>
        )
    }

    // Detail view: enhanced popover with metadata (source, tone, reason)
    // Gracefully handles missing metadata fields by only displaying available information
    const popoverContent = (
        <div className="ai-metadata-popover">
            {sourceMetadata?.tone && (
                <div className="metadata-row">
                    <span className="metadata-label">Tone:</span>
                    <span className="metadata-value">{titleCase(sourceMetadata.tone)}</span>
                </div>
            )}
            {sourceMetadata?.reason && (
                <div className="metadata-row">
                    <span className="metadata-label">Reason:</span>
                    <span className="metadata-value">{sourceMetadata.reason}</span>
                </div>
            )}
        </div>
    )

    return (
        <span className="ai-generated-badge">
            <Popover title={<strong>{basicTooltip}</strong>} content={popoverContent} trigger="hover">
                {badge}
            </Popover>
        </span>
    )
}
